import fs from 'fs/promises'
import fsSync from 'fs'
import path from 'path'
import { ErrorCode } from '@remotekit/shared'
import { RemoteKitError } from '@remotekit/shared'
import { validatePath } from '../security'
import type { CommandHandlerFn } from '../command-handler'

export interface FileHandlerOptions {
  allowedPaths: string[]
  fileBlacklist: string[]
  maxUploadSize: number
}

export function createFileHandlers(options: FileHandlerOptions): Record<string, CommandHandlerFn> {
  const { allowedPaths, fileBlacklist, maxUploadSize } = options

  function checkPath(targetPath: string) {
    validatePath(targetPath, allowedPaths, fileBlacklist)
  }

  return {
    'fs:list': async (params?: { path: string; showHidden?: boolean }) => {
      if (!params?.path) throw new RemoteKitError(ErrorCode.PATH_DENIED, '缺少 path 参数')
      checkPath(params.path)

      const entries = await fs.readdir(params.path, { withFileTypes: true })
      const result = []
      for (const entry of entries) {
        if (!params.showHidden && entry.name.startsWith('.')) continue
        const fullPath = path.join(params.path, entry.name)
        try {
          const stat = await fs.stat(fullPath)
          result.push({
            name: entry.name,
            path: fullPath,
            type: entry.isDirectory() ? 'directory' : entry.isSymbolicLink() ? 'symlink' : 'file',
            size: stat.size,
            modifiedAt: stat.mtime.toISOString(),
          })
        } catch { /* 跳过无权限文件 */ }
      }
      return result
    },

    'fs:stat': async (params?: { path: string }) => {
      if (!params?.path) throw new RemoteKitError(ErrorCode.PATH_DENIED, '缺少 path 参数')
      checkPath(params.path)

      try {
        const stat = await fs.stat(params.path)
        const lstat = await fs.lstat(params.path)
        return {
          name: path.basename(params.path),
          path: params.path,
          type: lstat.isSymbolicLink() ? 'symlink' : stat.isDirectory() ? 'directory' : 'file',
          size: stat.size,
          modifiedAt: stat.mtime.toISOString(),
          createdAt: stat.birthtime.toISOString(),
          accessedAt: stat.atime.toISOString(),
          isHidden: path.basename(params.path).startsWith('.'),
          permissions: (stat.mode & 0o777).toString(8),
        }
      } catch {
        throw new RemoteKitError(ErrorCode.FILE_NOT_FOUND, `文件不存在: ${params.path}`)
      }
    },

    'fs:read': async (params?: { path: string; encoding?: string; offset?: number; limit?: number }) => {
      if (!params?.path) throw new RemoteKitError(ErrorCode.PATH_DENIED, '缺少 path 参数')
      checkPath(params.path)

      try {
        const content = await fs.readFile(params.path, { encoding: (params.encoding as BufferEncoding) || 'utf-8' })
        const lines = content.split('\n')
        const offset = params.offset || 0
        const limit = params.limit || lines.length
        const sliced = lines.slice(offset, offset + limit)
        return { content: sliced.join('\n'), totalLines: lines.length }
      } catch {
        throw new RemoteKitError(ErrorCode.FILE_NOT_FOUND, `文件不存在: ${params.path}`)
      }
    },

    'fs:write': async (params?: { path: string; content: string; encoding?: string; append?: boolean }) => {
      if (!params?.path) throw new RemoteKitError(ErrorCode.PATH_DENIED, '缺少 path 参数')
      checkPath(params.path)

      const contentBuffer = Buffer.from(params.content, (params.encoding as BufferEncoding) || 'utf-8')
      if (contentBuffer.length > 1024 * 1024) {
        throw new RemoteKitError(ErrorCode.FILE_TOO_LARGE, '写入内容超过 1MB 限制')
      }

      if (params.append) {
        await fs.appendFile(params.path, params.content, { encoding: (params.encoding as BufferEncoding) || 'utf-8' })
      } else {
        await fs.writeFile(params.path, params.content, { encoding: (params.encoding as BufferEncoding) || 'utf-8' })
      }
      const stat = await fs.stat(params.path)
      return { success: true, size: stat.size }
    },

    'fs:mkdir': async (params?: { path: string; recursive?: boolean }) => {
      if (!params?.path) throw new RemoteKitError(ErrorCode.PATH_DENIED, '缺少 path 参数')
      checkPath(params.path)
      await fs.mkdir(params.path, { recursive: params.recursive !== false })
      return { created: true }
    },

    'fs:rename': async (params?: { from: string; to: string }) => {
      if (!params?.from || !params?.to) throw new RemoteKitError(ErrorCode.PATH_DENIED, '缺少参数')
      checkPath(params.from)
      checkPath(params.to)
      await fs.rename(params.from, params.to)
      return { success: true }
    },

    'fs:delete': async (params?: { path: string }) => {
      if (!params?.path) throw new RemoteKitError(ErrorCode.PATH_DENIED, '缺少 path 参数')
      checkPath(params.path)

      const stat = await fs.stat(params.path).catch(() => null)
      if (!stat) throw new RemoteKitError(ErrorCode.FILE_NOT_FOUND, `文件不存在: ${params.path}`)

      if (stat.isDirectory()) {
        await fs.rm(params.path, { recursive: true })
      } else {
        await fs.unlink(params.path)
      }
      return { success: true }
    },

    'fs:search': async (params?: { path: string; pattern: string; contentMatch?: string; maxResults?: number }) => {
      if (!params?.path || !params?.pattern) throw new RemoteKitError(ErrorCode.PATH_DENIED, '缺少参数')
      checkPath(params.path)

      const maxResults = params.maxResults || 100
      const results: any[] = []
      await searchDir(params.path, params.pattern, params.contentMatch, results, maxResults, allowedPaths, fileBlacklist)
      return results
    },
  }
}

async function searchDir(
  dir: string,
  pattern: string,
  contentMatch: string | undefined,
  results: any[],
  max: number,
  allowedPaths: string[],
  fileBlacklist: string[],
): Promise<void> {
  if (results.length >= max) return
  const entries = await fs.readdir(dir, { withFileTypes: true }).catch(() => [])
  const regex = new RegExp(pattern, 'i')

  for (const entry of entries) {
    if (results.length >= max) break
    const fullPath = path.join(dir, entry.name)

    try {
      validatePath(fullPath, allowedPaths, fileBlacklist)
    } catch {
      continue
    }

    if (regex.test(entry.name)) {
      const item: any = {
        path: fullPath,
        type: entry.isDirectory() ? 'directory' : 'file',
      }

      if (contentMatch && !entry.isDirectory()) {
        const content = await fs.readFile(fullPath, 'utf-8').catch(() => '')
        const lines = content.split('\n')
        const matches: any[] = []
        const contentRegex = new RegExp(contentMatch, 'i')
        for (let i = 0; i < lines.length; i++) {
          if (contentRegex.test(lines[i])) {
            matches.push({ line: i + 1, content: lines[i] })
          }
        }
        if (matches.length > 0) item.matches = matches
      }
      results.push(item)
    }

    if (entry.isDirectory()) {
      await searchDir(fullPath, pattern, contentMatch, results, max, allowedPaths, fileBlacklist)
    }
  }
}
